import {
  ankernokkel,
  styrerRapporten,
  type AnkerDto,
  type Meg,
  type PolitiskKommentarDto,
  type UtsendelseDto,
} from '@es/shared';
import { formaterFrist } from '../prognose/leveringer.js';

/**
 * Den politiske behandlingen, slik siden sier den.
 *
 * Rene funksjoner: hvilke faner Rapportering har for hvem, hvilke tråder som
 * hører til et sted i rapporten, og hvor langt en utsendelse har kommet.
 */

export type Fane = 'rapport' | 'okonomi' | 'maler' | 'behandling';

/**
 * Fanene kalleren har. En folkevalgt som ikke har noe annet med rapporten å
 * gjøre, har bare den politiske behandlingen — rapporten i arbeid og tallene
 * er administrasjonens.
 */
export function rapportfaner(meg: Meg): Fane[] {
  const styrer = styrerRapporten(meg);
  // Alle andre enn den som bare er folkevalgt, har rapporten og tallene — også
  // den som ikke har noe å se der ennå: siden forklarer hvorfor.
  const ansatt = !erBareFolkevalgt(meg);
  return [
    ...(ansatt ? (['rapport', 'okonomi'] as const) : []),
    ...(styrer ? (['maler'] as const) : []),
    ...(styrer || meg.omradetilganger.length > 0 || meg.utvalg.length > 0
      ? (['behandling'] as const)
      : []),
  ];
}

/**
 * Om kalleren bare er folkevalgt: sitter i et utvalg, og har ellers ingen
 * roller i kommunen. En slik bruker har bare rapportene til behandling å se,
 * og menyen viser ikke moduler som bare ville vært tomme.
 */
export function erBareFolkevalgt(meg: Meg): boolean {
  return (
    meg.utvalg.length > 0 &&
    !meg.kanAdministrere &&
    !styrerRapporten(meg) &&
    meg.tilganger.length === 0 &&
    meg.omradetilganger.length === 0
  );
}

/** Trådene som hører til akkurat dette stedet i rapporten. */
export function traderFor(
  kommentarer: readonly PolitiskKommentarDto[],
  anker: AnkerDto,
): PolitiskKommentarDto[] {
  const nokkel = ankernokkel(anker);
  return kommentarer.filter((k) => ankernokkel(k.anker) === nokkel);
}

/**
 * Hvor langt utsendelsen har kommet, i ord.
 *
 * @param idag Dagens dato i norsk tid, se `datoIOslo`.
 */
export function utsendelsestatus(
  u: Pick<UtsendelseDto, 'behandlet' | 'frist'>,
  idag: string,
): { tekst: string; behandlet: boolean } {
  const ar = Number(idag.slice(0, 4));
  if (u.behandlet !== null) {
    const sak = u.behandlet.saksnummer === null ? '' : ` (sak ${u.behandlet.saksnummer})`;
    return { tekst: `Behandlet ${formaterFrist(u.behandlet.dato, ar)}${sak}`, behandlet: true };
  }
  if (u.frist === null) return { tekst: 'Til behandling', behandlet: false };
  return {
    tekst: `Til behandling · ${idag > u.frist ? 'fristen for kommentarer var' : 'kommentarer innen'} ${formaterFrist(u.frist, ar)}`,
    behandlet: false,
  };
}
